import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <Link to="/" onClick={scrollToTop} className="inline-block hover:opacity-80 transition-opacity cursor-pointer">
                <img 
                  src="/images/logo-footer.svg" 
                  alt="Logo DEBECO, entreprise spécialisée en débarras et recyclage responsable" 
                  className="h-16 w-auto mb-3"
                  width="213"
                  height="64"
                  loading="lazy"
                  decoding="async"
                  data-testid="footer-logo"
                />
              </Link>
            </div>
            <p className="text-blue-100 text-sm mb-4">
              Entreprise spécialisée dans le débarras de mobilier, vide-maison et recyclage en Savoie, Haute-Savoie et l'Ain.
            </p>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/profile.php?id=61566361066237" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/debeco.fr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/company/deb-eco/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-emerald-400">Liens rapides</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/zones" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Zones d'intervention
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  À propos
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-emerald-400">Nos services</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services/maison" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Débarras de maison
                </Link>
              </li>
              <li>
                <Link to="/services/appartement" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Débarras d'appartement
                </Link>
              </li>
              <li>
                <Link to="/services/cave-grenier" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Débarras cave, grenier & garage
                </Link>
              </li>
              <li>
                <Link to="/services/succession" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Débarras succession
                </Link>
              </li>
              <li>
                <Link to="/services/professionnel" className="text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  Débarras professionnel
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-emerald-400">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href="tel:0451401617" className="flex items-start gap-3 text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  <Phone className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>04 51 40 16 17</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@debeco.fr" className="flex items-start gap-3 text-blue-100 hover:text-emerald-400 transition-colors text-sm">
                  <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>contact@debeco.fr</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-blue-100 text-sm">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Annecy, Genevois & Savoie</span>
                </div>
              </li>
            </ul>
            <div className="mt-4 p-3 bg-white/10 rounded-lg">
              <p className="text-xs text-blue-100">Intervention rapide</p>
              <p className="text-sm font-semibold text-emerald-400">24/48h</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-blue-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-blue-200 text-sm">
              © 2024 DEBECO. Tous droits réservés.
            </p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="text-blue-200 hover:text-emerald-400 transition-colors text-sm">
                Mentions légales
              </Link>
              <Link to="/mentions-legales" className="text-blue-200 hover:text-emerald-400 transition-colors text-sm">
                RGPD
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
